import React, { memo, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import useStore from '../../store/useStore';
import Sparkline from './Sparkline';

const GaugeWidget = memo(({ title, value, min = 0, max = 100, thresholds = [], history = null }) => {
  const animationsEnabled = useStore((state) => state.animationsEnabled);

  // Memoize percentage calculation (guard against null/undefined value)
  const percentage = useMemo(
    () => (value !== null && value !== undefined)
      ? Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100)
      : 0,
    [value, min, max]
  );

  // Memoize color determination
  const getColor = useCallback(() => {
    if (!thresholds || thresholds.length === 0) {
      return '#2889C6'; // Kinspeed blue
    }

    // Sort thresholds by value
    const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);

    for (let i = sortedThresholds.length - 1; i >= 0; i--) {
      if (value >= sortedThresholds[i].value) {
        return sortedThresholds[i].color || '#2889C6';
      }
    }

    return '#f44336'; // Red if below all thresholds
  }, [value, thresholds]);

  const color = useMemo(() => getColor(), [getColor]);

  // Memoize data to prevent unnecessary re-creation
  const data = useMemo(() => [
    { name: 'value', value: percentage },
    { name: 'remaining', value: 100 - percentage },
  ], [percentage]);

  const COLORS = useMemo(() => [color, 'rgba(255,255,255,0.08)'], [color]);

  return (
    <div className="widget gauge-widget">
      <div className="widget-header">
        <span className="widget-title">{title}</span>
      </div>
      <div className="gauge-widget-content">
        {/* Gauge chart container - pinned to top */}
        <div className="gauge-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="95%"
                startAngle={180}
                endAngle={0}
                innerRadius="68%"
                outerRadius="98%"
                dataKey="value"
                stroke="none"
                isAnimationActive={animationsEnabled}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Value overlaid in gauge center */}
        <div
          className={`gauge-value-overlay ${animationsEnabled ? 'animated' : ''}`}
          style={{ color }}
        >
          {value !== undefined && value !== null ? Math.round(value).toLocaleString() : '--'}
        </div>

        {/* Max value below gauge */}
        <div className="gauge-max-label">
          max: {max.toLocaleString()}
        </div>
        {history && history.length > 1 && (
          <div className="widget-sparkline gauge-sparkline" title="Recent values">
            <Sparkline values={history} color={color} />
          </div>
        )}
      </div>
    </div>
  );
});

GaugeWidget.displayName = 'GaugeWidget';

export default GaugeWidget;
